import React, { useEffect, useRef } from 'react';
import './AnimatedBackground.css';   // 👈 add this line


export default function AnimatedBackground() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d', { alpha: true });
    let rafId;
    const DPR = Math.min(window.devicePixelRatio || 1, 2);

    const stars = [];
    const rand = (a, b) => a + Math.random() * (b - a);

    function resize() {
      const w = window.innerWidth;
      const h = window.innerHeight;
      canvas.width = w * DPR;
      canvas.height = h * DPR;
      canvas.style.width = w + 'px';
      canvas.style.height = h + 'px';

      // regenerate specks proportionally to area
      stars.length = 0;
      const count = Math.max(40, Math.round((w * h) / 6000)); // ~80 on 1920x1080
      for (let i = 0; i < count; i++) {
        stars.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          r: rand(2.0, 1.5) * DPR,
          drift: 0.08 + Math.random() * 0.1,
          swayA: Math.random() * Math.PI * 1,
          swayS: 0.1 + Math.random() * 0.1
        });
      }
    }

    function draw() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // detect theme from <html data-theme="...">
      const isLight = document.documentElement.getAttribute('data-theme') === 'light';

      // subtle glow like prototype (top-left)
      const g = ctx.createRadialGradient(
        canvas.width * 0.2,
        canvas.height * 0.12,
        0,
        canvas.width * 0.2,
        canvas.height * 0.12,
        Math.max(canvas.width, canvas.height)
      );
      g.addColorStop(0, isLight ? 'rgba(255, 215, 0, 0.12)' : 'rgba(255, 215, 0, 0.10)');
      g.addColorStop(1, 'rgba(7, 13, 26, 0)');
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // drifting specks
      ctx.globalAlpha = isLight ? 0.85 : 0.9;
      for (const s of stars) {
        s.y -= s.drift * DPR;                     // slow upward drift
        s.x += Math.sin(s.swayA) * s.swayS * DPR; // tiny horizontal sway
        s.swayA += 0.01;

        if (s.y < -2) s.y = canvas.height + 2;

        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fillStyle = isLight
          ? 'rgba(255, 215, 0, 0.55)'   // light mode: gold specks
          : 'rgba(255, 215, 0, 0.45)';   // dark mode: gold specks
        ctx.fill();
      }

      rafId = requestAnimationFrame(draw);
    }

    resize();
    window.addEventListener('resize', resize);
    draw();

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <>
      {/* gradient layer lives under the canvas and animates via CSS */}
      <div className="animated-bg-gradient" aria-hidden="true" />
      <canvas ref={canvasRef} className="bg-canvas" aria-hidden="true" />
    </>
  );
}
